import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppProfile } from 'src/app/shared/app-profile/app-profile';
import { AuthService } from 'src/app/core/services/auth.service';
import { Router } from '@angular/router';

interface Rule {
  title: string;
  enabled: boolean;
  when: string;
  then: string;
  priority: string;
  image?: string;
}

interface RuleSet {
  id: string;
  title: string;
  status: string;
  rules: Rule[];
  description?: string;
}

@Component({
  selector: 'app-rulesets',
  templateUrl: './rulesets.component.html',
  styleUrls: ['./rulesets.component.scss']
})
export class RulesetsComponent implements OnInit {
  planDefinitionRulesApiResponse: any[] = [];
  ruleSets: RuleSet[] = [];
  selectedRuleSetId: string = '';
  selectedRuleSet: RuleSet | null = null;
  ruleTypes = ['Usage', 'Health', 'Prescription'];
  selectedRuleType = 'Usage';
  planDefinitionRules: Rule[] = [];

  // Modal and form state
  showAddRuleModal = false;
  showEditRuleModal = false;
  showAddRuleSetModal = false;
  showEditRuleSetModal = false;
  ruleToEdit: Rule | null = null;
  ruleSetToEdit: RuleSet | null = null;

  // Dual list state (for ruleset editing)
  availableItems: string[] = [];
  selectedItems: string[] = [];
  availableFilter = '';
  selectedFilter = '';

  // Pagination state
  page = 1;
  pageSize = 3;
  get pagedRules() {
    // Use selectedRuleSet.rules for display, paginated
    if (!this.selectedRuleSet || !Array.isArray(this.selectedRuleSet.rules)) return [];
    const start = (this.page - 1) * this.pageSize;
    return this.planDefinitionRules.slice(start, start + this.pageSize);
  }
  get totalPages() {
    return Math.ceil(this.planDefinitionRules.length / this.pageSize);
  }

  get filteredAvailableItems() {
    return this.availableItems.filter(item =>
      item.toLowerCase().includes(this.availableFilter.toLowerCase()) &&
      !this.selectedItems.includes(item)
    );
  }
  get filteredSelectedItems() {
    return this.selectedItems.filter(item =>
      item.toLowerCase().includes(this.selectedFilter.toLowerCase())
    );
  }

  showAddRuleSetFab = false;

  constructor(private http: HttpClient,
              private appProfile: AppProfile,
              private authService: AuthService,
              private router: Router) {
    // Only show FAB if on /rules/rulesets (exact match, optionally with trailing slash)
    const fullPath = this.router.url;
    this.showAddRuleSetFab = /\/rules\/rulesets\/?$/.test(fullPath);
  }

  ngOnInit() {
    this.fetchPlanDefinitionRules();
    // this.fetchPlanDefinitionRulesAndPopulate();
    this.fetchRuleSets();
  }

  fetchRulesForRuleSet(ruleSetId: string) {
    const baseHeaders = this.getCommonHeaders();
    if (!baseHeaders) return;
    const headers = baseHeaders.set('api-version', '2.0.0');
    const orgId = this.getCommonHeaders().get('org');
    this.http.get<any>(`${this.appProfile.ampmUs}/pes/fhir/${orgId}/RuleSet/${ruleSetId}/Rules`, { headers })
      .subscribe(res => {
        if (res && res.entry) {
          this.planDefinitionRules = res.entry.flatMap((e: any) => {
            const resource = e.resource;
            if (!resource.action) return [];
            return resource.action.map((action: any) => ({
              title: resource.title || resource.name || 'Untitled Rule',
              enabled: true,
              when: action.textEquivalent || '',
              then: 'Add a task to follow up with the patient. With a priority of Medium',
              priority: 'Medium'
            } as Rule));
          });
        }
      });
  }

  // fetchPlanDefinitionRulesAndPopulate() {
  //   const baseHeaders = this.getCommonHeaders();
  //   if (!baseHeaders) return;
  //   const headers = baseHeaders.set('api-version', '2.0.0');
  //   this.http.get<any>(`${this.appProfile.rulesAPI}/pes/fhir/2faca8f1-c381-4122-a634-471e1075dd8d/PlanDefinition`, { headers }).subscribe(res => {
  //     if (res && res.entry) {
  //       this.planDefinitionRules = res.entry.flatMap((e: any) => {
  //         const resource = e.resource;
  //         if (!resource.action) return [];
  //         return resource.action.map((action: any) => ({
  //           id: resource.id,
  //           title: resource.title || resource.name || 'Untitled Rule'
  //         }));
  //       });
  //       // Populate availableItems with {id, title} for dual-list
  //       // this.availableItems = this.planDefinitionRules.map(rule => ({ id: (rule as any).id, title: rule.title }));
  //     }
  //   });
  // }

  private getCommonHeaders(): HttpHeaders {
    const accessToken = this.authService.accessToken;
    if (!accessToken) {
      console.error('Access token is missing. Please ensure you are logged in.');
      alert('Access token is missing. Please log in again.');
      return;
    }
      return new HttpHeaders({
        'X-tenant-Id': '2faca8f1-c381-4122-a634-471e1075dd8d',
        'Content-Type': 'application/json',
        'org': '2faca8f1-c381-4122-a634-471e1075dd8d',
        'Authorization': `Bearer ${accessToken}`,
      });
    }

  fetchRuleSets() {
    const baseHeaders = this.getCommonHeaders();
    if (!baseHeaders) return;
    const headers = baseHeaders.set('api-version', '2.0.0');
    const orgId = this.getCommonHeaders().get('org');
    this.http.get<any>(`${this.appProfile.ampmUs}/pes/fhir/${orgId}/RuleSet`, { headers }).subscribe(res => {
      if (res && res.entry) {
        this.ruleSets = res.entry.map((e: any) => ({
          id: e.resource.id,
          title: e.resource.title || e.resource.name || 'Untitled',
          status: e.resource.status,
          rules: []
        }));
        if (this.ruleSets.length > 0) {
          this.selectedRuleSetId = this.ruleSets[0].id;
          this.selectedRuleSet = this.ruleSets[0];
          this.fetchRulesForRuleSet(this.selectedRuleSetId);
        }
      }
    });
  }

  fetchPlanDefinitionRules() {
    const baseHeaders = this.getCommonHeaders();
    if (!baseHeaders) return;
    const headers = baseHeaders.set('api-version', '2.0.0');
    const orgId = this.getCommonHeaders().get('org');
    this.http.get<any>(`${this.appProfile.ampmUs}/pes/fhir/${orgId}/PlanDefinition`, { headers }).subscribe(res => {
      if (res && res.entry) {
        this.planDefinitionRulesApiResponse = res.entry;
        this.planDefinitionRules = res.entry.flatMap((e: any) => {
          const resource = e.resource;
          if (!resource.action) return [];
          return resource.action.map((action: any) => ({
            title: resource.title || resource.name || 'Untitled Rule',
            enabled: true,
            when: action.textEquivalent || '',
            then: 'Add a task to follow up with the patient. With a priority of Medium',
            priority: 'Medium'
          } as Rule));
        });
        // Set availableItems to all PlanDefinition resource.name values
        this.availableItems = res.entry
          .map((e: any) => e.resource.name)
          .filter((name: any) => !!name);
      }
    });
  }

  extractRulesFromResource(resource: any): Rule[] {
    if (!resource.action) return [];
    const rules: Rule[] = [];
    // Helper to recursively extract rules from nested actions
    const extract = (actions: any[]) => {
      actions.forEach(action => {
        // If this action has a definitionCanonical, treat as a rule reference (could be expanded if more details are available)
        if (action.definitionCanonical) {
          rules.push({
            title: action.definitionCanonical.split('/').pop() || 'Referenced Rule',
            enabled: true,
            when: '',
            then: '',
            priority: 'Medium'
          });
        }
        // If this action has dynamicValue, treat as a rule
        if (action.dynamicValue) {
          const when = action.dynamicValue.map((dv: any) => {
            if (dv.path && dv.expression && dv.expression.expression) {
              return `${dv.path}: ${dv.expression.expression}`;
            }
            return JSON.stringify(dv);
          }).join('; ');
          rules.push({
            title: action.title || 'Untitled Rule',
            enabled: true,
            when,
            then: '',
            priority: 'Medium'
          });
        }
        // If this action has nested actions, recurse
        if (action.action) {
          extract(action.action);
        }
      });
    };
    extract(resource.action);
    return rules;
  }

  selectRuleSet(event: any) {
    const id = event.target ? event.target.value : event;
    this.selectedRuleSetId = id;
    this.selectedRuleSet = this.ruleSets.find(rs => rs.id === id) || null;
    this.page = 1;
    if (id) {
      this.fetchRulesForRuleSet(id);
    }
  }

  setRuleType(type: string) {
    this.selectedRuleType = type;
    // Optionally filter rules by type here
  }

  // Rule actions
  toggleRule(rule: Rule) {
    rule.enabled = !rule.enabled;
  }
  editRule(rule: Rule) {
    this.ruleToEdit = { ...rule };
    this.showEditRuleModal = true;
  }
  saveRuleEdit() {
    if (this.selectedRuleSet && this.ruleToEdit) {
      const idx = this.selectedRuleSet.rules.findIndex(r => r.title === this.ruleToEdit!.title);
      if (idx > -1) this.selectedRuleSet.rules[idx] = { ...this.ruleToEdit };
    }
    this.closeEditRuleModal();
  }
  closeEditRuleModal() {
    this.showEditRuleModal = false;
    this.ruleToEdit = null;
  }
  addRule() {
    this.ruleToEdit = { title: '', enabled: true, when: '', then: '', priority: 'Medium' };
    this.showAddRuleModal = true;
  }
  saveNewRule() {
    if (this.selectedRuleSet && this.ruleToEdit) {
      this.selectedRuleSet.rules.push({ ...this.ruleToEdit });
    }
    this.closeAddRuleModal();
  }
  closeAddRuleModal() {
    this.showAddRuleModal = false;
    this.ruleToEdit = null;
  }

  // Ruleset actions
  addRuleSet() {
    this.ruleSetToEdit = {
      id: '',
      title: '',
      status: 'active',
      rules: [],
      description: '',
    };
    this.selectedItems = [];
    this.availableFilter = '';
    this.selectedFilter = '';
    this.showAddRuleSetModal = true;
  }
  saveNewRuleSet() {
      // Map selectedItems (titles) to their corresponding fullUrl values from PlanDefinition API response
      const planDefs = this.planDefinitionRulesApiResponse || [];
      const selectedDefinitions = this.selectedItems
        .map(title => {
          const found = planDefs.find((e: any) => e.resource.title === title || e.resource.name === title);
          return found ? { definitionCanonical: found.fullUrl } : null;
        })
        .filter((def: any) => !!def);
      const payload = {
        resourceType: 'PlanDefinition',
        name: 'RULE_SET',
        title: this.ruleSetToEdit.title,
        status: 'active',
        action: [
          {
            dynamicValue: [
              {
                path: 'isDefault',
                expression: {
                  language: 'text/cql',
                  expression: 'false'
                }
              }
            ],
            action: selectedDefinitions
          }
        ]
      };
      const baseHeaders = this.getCommonHeaders();
      if (!baseHeaders) return;
      const headers = baseHeaders.set('api-version', '2.0.0');
      const orgId = this.getCommonHeaders().get('org');
      this.http.post<any>(`${this.appProfile.ampmUs}/pes/fhir/${orgId}/RuleSet`, payload, { headers }).subscribe({
        next: (res) => {
          this.closeAddRuleSetModal();
        },
        error: (err) => {
          alert('Failed to create RuleSet: ' + (err?.message || 'Unknown error'));
        }
      });
  }
  closeAddRuleSetModal() {
    this.showAddRuleSetModal = false;
    this.ruleSetToEdit = null;
  }
  editRuleSet() {
    if (this.selectedRuleSet) {
      this.ruleSetToEdit = { ...this.selectedRuleSet };
      this.showEditRuleSetModal = true;
    }
  }
  saveRuleSetEdit() {
    if (this.ruleSetToEdit) {
      const idx = this.ruleSets.findIndex(rs => rs.id === this.ruleSetToEdit!.id);
      if (idx > -1) this.ruleSets[idx] = { ...this.ruleSetToEdit };
      if (this.selectedRuleSetId === this.ruleSetToEdit.id) {
        this.selectedRuleSet = { ...this.ruleSetToEdit };
      }
    }
    this.closeEditRuleSetModal();
  }
  closeEditRuleSetModal() {
    this.showEditRuleSetModal = false;
    this.ruleSetToEdit = null;
  }

  // Dual list selection logic for Add RuleSet modal
  selectAvailableItem(item: string) {
    if (!this.selectedItems.includes(item)) {
      this.selectedItems.push(item);
      this.availableItems = this.availableItems.filter(i => i !== item);
    }
  }
  selectSelectedItem(item: string) {
    this.selectedItems = this.selectedItems.filter(i => i !== item);
    this.availableItems.push(item);
  }
  moveToSelected() {
    const filtered = this.filteredAvailableItems;
    filtered.forEach(item => this.selectAvailableItem(item));
  }
  moveToAvailable() {
    const filtered = this.filteredSelectedItems;
    filtered.forEach(item => this.selectSelectedItem(item));
  }

  // Pagination
  nextPage() {
    if (this.page < this.totalPages) this.page++;
  }

  prevPage() {
    if (this.page > 1) this.page--;
  }
}
